import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { Form } from '@anytag/shared-atoms';
import { SelectedRangeOption } from '@anytag/shared-types';
import { useReportFilterOptions } from '@anytag/shared-hooks';
import { SelectorField, RangeDatePickerField, AsyncSearchCampaignField } from '@anytag/shared-molecules';
import { ReportFilterFormValues, ReportFilterFormKeys } from './schema-types';

interface TemplateProps {
  onSubmit?: () => void;
  page?: number;
}

export const Template = ({ onSubmit, page }: TemplateProps) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { watch } = useFormContext<ReportFilterFormValues>();
  const { allCampaignTypes, allCountryOptions, allSocialMediaOptions, allCampaignCategoryOptions } =
    useReportFilterOptions();

  const [endDateValue, startDateValue] = watch(['endDate', 'startDate']);
  const isAllTime = !startDateValue && !endDateValue;

  return (
    <Form.Fields>
      <AsyncSearchCampaignField page={page} name="campaigns" onSubmit={onSubmit} className="basis-full" multiple />

      <RangeDatePickerField<ReportFilterFormKeys>
        onSubmit={onSubmit}
        clearTitle={t('All time')}
        name={{ startDate: 'startDate', endDate: 'endDate' }}
        customInputTextNode={isAllTime ? <p>{t('All time')}</p> : undefined}
        rangeList={[
          SelectedRangeOption.TODAY,
          SelectedRangeOption.LAST_SEVEN_DAYS,
          SelectedRangeOption.TODAY_TO_MONTH_AGO,
          SelectedRangeOption.THIS_MONTH,
          SelectedRangeOption.LAST_MONTH,
          SelectedRangeOption.CUSTOM,
          SelectedRangeOption.CLEAR
        ]}
      />

      <SelectorField<ReportFilterFormKeys>
        name="campaignTypes"
        onSubmit={onSubmit}
        options={allCampaignTypes}
        placeholder={t<string>('Selector.Campaign Type')}
        multiple
      />

      <SelectorField<ReportFilterFormKeys>
        name="countryId"
        onSubmit={onSubmit}
        options={allCountryOptions}
        placeholder={t<string>('Selector.Country')}
      />

      <SelectorField<ReportFilterFormKeys>
        name="socialMedias"
        onSubmit={onSubmit}
        options={allSocialMediaOptions}
        placeholder={t<string>('Selector.Social Platform')}
        multiple
      />

      {isAdminStaff ? (
        <SelectorField<ReportFilterFormKeys>
          onSubmit={onSubmit}
          name="campaignCategoryIds"
          options={allCampaignCategoryOptions}
          placeholder={t<string>('Selector.Campaign Category')}
          multiple
        />
      ) : null}
    </Form.Fields>
  );
};
